/**
 * MasterProducts — Sprint 72/73 (SUPER_ADMIN cross-store master control).
 *
 * Lists every product across every store. Inline edit for MRP, Selling Price,
 * and Stock Count. Backed by SA-only endpoints:
 *   GET   /api/v1/admin/products/all?q=&store_id=
 *   PATCH /api/v1/admin/products/:id
 *
 * Sprint 73 — Smart Search (name / SKU / barcode) + Store Filter dropdown
 * with its own local search so admins on 18+ stores can pick fast.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import * as XLSX from "xlsx";
import { api, money, errMsg, auth } from "@/lib/api";
import { isDataUrl, uploadDataUrl } from "@/lib/cloudinary";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Package, Search, RefreshCw, Store as StoreIcon, AlertCircle, ChevronDown, Check, X, Download, Upload, DatabaseZap } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { Navigate } from "react-router-dom";

const BRAND_RED = "#E32636";

/** Small inline-editable numeric cell. Enter or blur commits, Esc reverts. */
function EditableNumCell({ value, onSave, prefix = "", suffix = "", "data-testid": testid, min = 0, step = "any" }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(value ?? "");
  const inputRef = useRef(null);

  useEffect(() => { if (!editing) setVal(value ?? ""); }, [value, editing]);
  useEffect(() => { if (editing) inputRef.current?.focus(); }, [editing]);

  const commit = async () => {
    const num = val === "" ? null : Number(val);
    if (num !== null && Number.isNaN(num)) {
      toast.error("Please enter a valid number");
      return;
    }
    if (num !== null && num < min) {
      toast.error(`Value cannot be less than ${min}`);
      return;
    }
    setEditing(false);
    if (num !== (value ?? null)) {
      try { await onSave(num); } catch (e) { toast.error(errMsg(e)); }
    }
  };

  if (!editing) {
    return (
      <button
        onClick={() => setEditing(true)}
        className="w-full text-left px-2 py-1.5 rounded-md hover:bg-slate-50 border border-transparent hover:border-slate-200 transition text-[13px] font-semibold text-slate-900 tabular-nums"
        data-testid={testid}
      >
        {prefix}{value ?? "—"}{suffix}
      </button>
    );
  }
  return (
    <div className="flex items-center gap-1">
      <Input
        ref={inputRef}
        type="number"
        step={step}
        min={min}
        value={val}
        onChange={(e) => setVal(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") commit();
          if (e.key === "Escape") { setEditing(false); setVal(value ?? ""); }
        }}
        onBlur={commit}
        className="h-8 text-[13px] tabular-nums w-24"
        data-testid={`${testid}-input`}
      />
    </div>
  );
}

/**
 * StoreFilterPicker — dropdown with a searchable list of every store.
 * Uses `/api/v1/stores?limit=100` (SA-only) — safe here because this page is
 * gated by role above.
 */
function StoreFilterPicker({ value, onChange }) {
  const [open, setOpen] = useState(false);
  const [stores, setStores] = useState([]);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");
  const searchRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/stores?limit=100");
        if (!cancelled) setStores(data.items || []);
      } catch (e) {
        // Non-fatal — filter will just show empty state.
        if (!cancelled) setStores([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Focus the internal search when the popover opens.
  useEffect(() => { if (open) setTimeout(() => searchRef.current?.focus(), 60); }, [open]);

  const filtered = useMemo(() => {
    const t = q.trim().toLowerCase();
    if (!t) return stores;
    return stores.filter((s) =>
      (s.name || "").toLowerCase().includes(t) ||
      (s.slug || "").toLowerCase().includes(t) ||
      (s.code || "").toLowerCase().includes(t),
    );
  }, [stores, q]);

  const selected = value ? stores.find((s) => s.id === value) : null;
  const label = selected ? selected.name : "All stores";

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          className={`h-10 px-3 rounded-lg border text-[13px] font-semibold flex items-center gap-2 shrink-0 transition ${
            value
              ? "border-transparent text-white"
              : "bg-white border-slate-200 text-slate-700 hover:border-slate-300"
          }`}
          style={value ? { backgroundColor: BRAND_RED } : undefined}
          data-testid="master-products-store-filter"
        >
          <StoreIcon className="w-3.5 h-3.5" />
          <span className="max-w-[180px] truncate">{label}</span>
          <ChevronDown className="w-3.5 h-3.5" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        sideOffset={6}
        className="w-72 p-2 rounded-xl border border-slate-200 shadow-xl"
        data-testid="master-products-store-picker"
      >
        <div className="relative mb-2">
          <Search className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input
            ref={searchRef}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search store…"
            className="pl-8 pr-8 h-9 text-[13px] bg-white border-slate-200"
            data-testid="master-products-store-search"
          />
          {q && (
            <button onClick={() => setQ("")} className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600" aria-label="Clear">
              <X className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
        <div className="max-h-72 overflow-y-auto space-y-0.5">
          <button
            className={`w-full flex items-center justify-between gap-2 px-2.5 py-2 rounded-lg text-left text-[13px] hover:bg-slate-50 ${!value ? "bg-red-50/60" : ""}`}
            onClick={() => { onChange(""); setOpen(false); setQ(""); }}
            data-testid="master-products-store-opt-all"
          >
            <span className="font-semibold text-slate-800">All stores</span>
            {!value && <Check className="w-4 h-4" style={{ color: BRAND_RED }} />}
          </button>
          {loading && (
            <div className="px-2 py-3 text-[12px] text-slate-400 text-center">Loading stores…</div>
          )}
          {!loading && filtered.length === 0 && (
            <div className="px-2 py-3 text-[12px] text-slate-400 text-center">No stores match</div>
          )}
          {filtered.map((s) => (
            <button
              key={s.id}
              className={`w-full flex items-center justify-between gap-2 px-2.5 py-2 rounded-lg text-left text-[13px] hover:bg-slate-50 ${value === s.id ? "bg-red-50/60" : ""}`}
              onClick={() => { onChange(s.id); setOpen(false); setQ(""); }}
              data-testid={`master-products-store-opt-${s.id}`}
            >
              <div className="min-w-0 flex-1">
                <div className="font-semibold text-slate-800 truncate">{s.name}</div>
                <div className="text-[11px] text-slate-500 truncate">
                  {s.code || s.slug || "—"}
                </div>
              </div>
              {value === s.id && <Check className="w-4 h-4 shrink-0" style={{ color: BRAND_RED }} />}
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}

export default function MasterProducts() {
  const { user } = useAuth();

  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit] = useState(50);
  const [q, setQ] = useState("");
  const [debouncedQ, setDebouncedQ] = useState("");
  const [storeFilter, setStoreFilter] = useState(""); // "" = all stores
  const [loading, setLoading] = useState(false);
  const [savingId, setSavingId] = useState(null);

  // Debounce search
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(q.trim()), 300);
    return () => clearTimeout(t);
  }, [q]);

  // Reset to page 1 when filters change
  useEffect(() => { setPage(1); }, [debouncedQ, storeFilter]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: String(page), limit: String(limit) });
      if (debouncedQ) params.set("q", debouncedQ);
      if (storeFilter) params.set("store_id", storeFilter);
      const { data } = await api.get(`/admin/products/all?${params.toString()}`);
      setItems(data.items || []);
      setTotal(data.total || 0);
    } catch (e) {
      toast.error(errMsg(e));
      setItems([]); setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [page, limit, debouncedQ, storeFilter]);

  useEffect(() => { load(); }, [load]);

  const patchOne = useCallback(async (id, patch, optimistic) => {
    setSavingId(id);
    // optimistic update
    setItems((prev) => prev.map((it) => it.id === id ? { ...it, ...optimistic } : it));
    try {
      const { data } = await api.patch(`/admin/products/${id}`, patch);
      setItems((prev) => prev.map((it) => it.id === id ? {
        ...it,
        mrp: data.mrp,
        selling_price: data.selling_price,
        stock: data.stock,
      } : it));
      toast.success("Saved");
    } catch (e) {
      toast.error(errMsg(e));
      // revert
      await load();
    } finally {
      setSavingId(null);
    }
  }, [load]);

  const orphanCount = useMemo(() => items.filter((i) => i.is_orphan).length, [items]);
  const pageCount = Math.max(1, Math.ceil(total / limit));

  // ---------- Sync & Restore Data (Sprint 74) ----------
  const [syncing, setSyncing] = useState(false);
  const runSyncRestore = useCallback(async () => {
    if (syncing) return;
    if (!window.confirm("Sync & Restore will move all products, variants and inventory into the babustore tenant. This is idempotent and never deletes. Continue?")) return;
    setSyncing(true);
    const t = toast.loading("Syncing all product data into babustore…");
    try {
      const { data } = await api.post("/admin/products/sync-repair");
      toast.dismiss(t);
      const orph = data?.orphan || {};
      const cons = data?.consolidated || {};
      const totalMoved = (orph.products || 0) + (cons.products || 0);
      toast.success(`Sync complete — ${totalMoved} products linked to babustore (variants ${(orph.product_variants || 0) + (cons.product_variants || 0)}, inventory ${(orph.inventory_items || 0) + (cons.inventory_items || 0)})`);
      await load();
    } catch (e) {
      toast.dismiss(t);
      toast.error(errMsg(e, "Sync failed"));
    } finally { setSyncing(false); }
  }, [syncing, load]);

  // ---------- Export Excel (all stores, master view) ----------
  const [exporting, setExporting] = useState(false);
  const runExport = useCallback(async () => {
    if (exporting) return;
    setExporting(true);
    const t = toast.loading("Preparing Excel export…");
    try {
      // Paginate over /admin/products/all to gather every product across stores.
      const first = await api.get(`/admin/products/all?page=1&limit=200`);
      const totalRows = first.data.total || (first.data.items || []).length;
      const allRows = [...(first.data.items || [])];
      const pages = Math.ceil(totalRows / 200);
      if (pages > 1) {
        const rest = await Promise.all(
          Array.from({ length: pages - 1 }).map((_, i) =>
            api.get(`/admin/products/all?page=${i + 2}&limit=200`).then((r) => r.data.items || []).catch(() => []),
          ),
        );
        rest.forEach((chunk) => allRows.push(...chunk));
      }
      const rows = allRows.map((p) => ({
        "Product Name":  p.name || "",
        "SKU":           p.sku || "",
        "Store":         p.store?.name || (p.is_orphan ? "ORPHAN" : ""),
        "Store Code":    p.store?.code || "",
        "MRP":           Number(p.mrp || 0),
        "Selling Price": Number(p.selling_price || 0),
        "Stock":         Number(p.stock || 0),
        "Status":        p.is_active === false ? "Inactive" : "Active",
      }));
      const ws = XLSX.utils.json_to_sheet(rows, {
        header: ["Product Name", "SKU", "Store", "Store Code", "MRP", "Selling Price", "Stock", "Status"],
      });
      ws["!cols"] = [{ wch: 34 }, { wch: 18 }, { wch: 22 }, { wch: 14 }, { wch: 10 }, { wch: 14 }, { wch: 10 }, { wch: 10 }];
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Master Products");
      const ts = new Date().toISOString().slice(0, 10);
      XLSX.writeFile(wb, `master_products_${ts}.xlsx`);
      toast.dismiss(t);
      toast.success(`Exported ${rows.length} product${rows.length === 1 ? "" : "s"}`);
    } catch (e) {
      toast.dismiss(t);
      toast.error(errMsg(e, "Export failed"));
    } finally { setExporting(false); }
  }, [exporting]);

  // ---------- Import Excel (create/update against babustore) ----------
  const fileInputRef = useRef(null);
  const [importing, setImporting] = useState(false);
  const [importReport, setImportReport] = useState(null);

  const handleImportFile = useCallback(async (e) => {
    const file = e.target.files?.[0];
    if (fileInputRef.current) fileInputRef.current.value = "";
    if (!file) return;
    const ext = file.name.split(".").pop().toLowerCase();
    if (!["xlsx", "xls", "csv"].includes(ext)) {
      toast.error("Only .xlsx, .xls or .csv files are supported");
      return;
    }
    setImporting(true);
    setImportReport(null);
    const t = toast.loading("Parsing file…");
    try {
      // 1) Resolve babustore id (all master data lives here after repair).
      const storesRes = await api.get("/stores?limit=100");
      const stores = storesRes.data?.items || [];
      const babustore = stores.find((s) => s.slug === "babustore") || stores.find((s) => s.code === "BABUSTORE") || stores[0];
      if (!babustore) throw new Error("babustore not found. Run Sync & Restore first.");
      // Route ALL following API calls to babustore via X-Store-Id (interceptor).
      auth.setStoreId(babustore.id, "SUPER_ADMIN");

      // 2) Parse file.
      const buf = await file.arrayBuffer();
      const wb = XLSX.read(buf, { type: "array" });
      const ws = wb.Sheets[wb.SheetNames[0]];
      const raw = XLSX.utils.sheet_to_json(ws, { defval: "", raw: false });
      if (raw.length === 0) throw new Error("No rows found in file");

      // 3) Load existing SKUs so we can update-vs-create.
      const fetchAll = async (path) => {
        const out = []; let page = 1;
        while (true) {
          const { data } = await api.get(`${path}${path.includes("?") ? "&" : "?"}page=${page}&limit=100`);
          const items = data?.items || [];
          out.push(...items);
          if (items.length < 100) break;
          page++;
          if (page > 200) break;
        }
        return out;
      };
      const [cats, brands, existing] = await Promise.all([
        fetchAll("/categories").catch(() => []),
        fetchAll("/brands").catch(() => []),
        fetchAll("/products").catch(() => []),
      ]);
      const norm = (v) => String(v || "").trim().toLowerCase();
      const skuMap = new Map(existing.filter((p) => p.sku).map((p) => [norm(p.sku), p.id]));

      // 4) Process each row.
      let created = 0, updated = 0, failed = 0;
      const failures = [];
      toast.dismiss(t);
      const tp = toast.loading(`Importing 0 / ${raw.length}…`);
      for (let i = 0; i < raw.length; i++) {
        const r = raw[i];
        // Normalise headers → strip trailing " *" from required-flag templates.
        const row = {};
        for (const [k, v] of Object.entries(r)) {
          row[k.replace(/\s*\*+\s*$/, "").trim()] = typeof v === "string" ? v.trim() : v;
        }
        const name = row["Product Name"] || row.Name;
        if (!name) { failed++; failures.push({ row: i + 2, reason: "Missing Product Name" }); continue; }
        try {
          const sku = row.SKU || row["SKU / Code"] || "";
          const existingId = sku ? skuMap.get(norm(sku)) : null;
          const catName = row.Category || row["Category Name"] || "";
          const category = catName ? cats.find((c) => !c.parent_id && norm(c.name) === norm(catName)) : null;
          const brandName = row.Brand || "";
          const brand = brandName ? brands.find((b) => norm(b.name) === norm(brandName)) : null;
          const mrpVal = row.MRP !== undefined && row.MRP !== "" ? Number(row.MRP) : null;
          const spVal = row["Selling Price"] !== undefined && row["Selling Price"] !== "" ? Number(row["Selling Price"]) : null;
          const stockVal = row.Stock !== undefined && row.Stock !== "" && !Number.isNaN(Number(row.Stock)) ? Number(row.Stock) : null;

          if (existingId) {
            const patch = {};
            if (mrpVal !== null && !Number.isNaN(mrpVal)) patch.mrp = mrpVal;
            if (spVal !== null && !Number.isNaN(spVal)) patch.selling_price = spVal;
            if (row.Description) patch.description = String(row.Description);
            if (Object.keys(patch).length) await api.patch(`/products/${existingId}`, patch);
            if (stockVal !== null && stockVal > 0) {
              await api.post("/inventory/adjust", { product_id: existingId, variant_id: null, delta_qty: stockVal, reason: "master_import" }).catch(() => {});
            }
            updated++;
          } else {
            if (mrpVal === null || spVal === null) { failed++; failures.push({ row: i + 2, reason: "Missing MRP or Selling Price" }); continue; }
            const payload = {
              name: String(name),
              description: row.Description || "",
              mrp: mrpVal,
              selling_price: spVal,
              gst_rate: Number(row["GST %"] || 0),
              is_active: norm(row.Status) === "inactive" ? false : true,
            };
            if (sku) payload.sku = sku;
            if (row.Barcode) payload.barcode = String(row.Barcode);
            if (row["HSN Code"]) payload.hsn_code = String(row["HSN Code"]);
            if (row["Image URL 1"] || row["Image URL"]) payload.thumbnail_url = row["Image URL 1"] || row["Image URL"];
            if (row["Cost Price"] !== undefined && row["Cost Price"] !== "") payload.cost_price = Number(row["Cost Price"]);
            if (row.Weight !== undefined && row.Weight !== "") payload.weight = Number(row.Weight);
            if (row.Unit) payload.unit = String(row.Unit);
            if (brand?.id) payload.brand_id = brand.id;
            if (category?.id) payload.category_id = category.id;
            const { data: newProd } = await api.post("/products", payload);
            if (stockVal !== null && stockVal > 0 && newProd?.id) {
              await api.post("/inventory/adjust", { product_id: newProd.id, variant_id: null, delta_qty: stockVal, reason: "master_import" }).catch(() => {});
            }
            if (sku) skuMap.set(norm(sku), newProd?.id);
            created++;
          }
        } catch (err) {
          failed++;
          failures.push({ row: i + 2, reason: errMsg(err, "Import row failed") });
        }
        if ((i + 1) % 5 === 0 || i === raw.length - 1) {
          toast.loading(`Importing ${i + 1} / ${raw.length}…`, { id: tp });
        }
      }
      toast.dismiss(tp);
      setImportReport({ created, updated, failed, failures, total: raw.length });
      toast.success(`Import complete — created ${created}, updated ${updated}, failed ${failed}`);
      await load();
    } catch (e) {
      toast.dismiss(t);
      toast.error(errMsg(e, "Import failed"));
    } finally { setImporting(false); }
  }, [load]);

  if (user && user.role !== "SUPER_ADMIN") {
    return <Navigate to="/admin" replace />;
  }

  const clearFilters = () => { setQ(""); setStoreFilter(""); };
  const hasFilters = !!q || !!storeFilter;

  return (
    <div className="space-y-5" data-testid="master-products-page">
      <PageHeader
        title="Master Product Control"
        subtitle="Cross-store product master · Smart search (name / SKU / barcode), store filter, and inline edit"
        icon={Package}
        actions={
          <div className="flex items-center gap-2 flex-wrap">
            <Button
              variant="outline"
              onClick={runSyncRestore}
              disabled={syncing}
              className="h-9 rounded-lg"
              data-testid="master-products-sync-btn"
            >
              <DatabaseZap className={`w-4 h-4 mr-1.5 ${syncing ? "animate-pulse" : ""}`} />
              {syncing ? "Syncing…" : "Sync & Restore Data"}
            </Button>
            <Button
              variant="outline"
              onClick={runExport}
              disabled={exporting}
              className="h-9 rounded-lg"
              data-testid="master-products-export-btn"
            >
              <Download className="w-4 h-4 mr-1.5" />
              {exporting ? "Exporting…" : "Export Excel"}
            </Button>
            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx,.xls,.csv"
              className="hidden"
              onChange={handleImportFile}
              data-testid="master-products-import-file"
            />
            <Button
              variant="default"
              onClick={() => fileInputRef.current?.click()}
              disabled={importing}
              className="h-9 rounded-lg text-white"
              style={{ backgroundColor: BRAND_RED }}
              data-testid="master-products-import-btn"
            >
              <Upload className={`w-4 h-4 mr-1.5 ${importing ? "animate-pulse" : ""}`} />
              {importing ? "Importing…" : "Import Excel"}
            </Button>
          </div>
        }
      />

      {/* Import report banner */}
      {importReport && (
        <div className="sa-card p-3 flex items-center gap-3 flex-wrap" data-testid="master-products-import-report">
          <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100">Created: {importReport.created}</Badge>
          <Badge className="bg-blue-100 text-blue-700 hover:bg-blue-100">Updated: {importReport.updated}</Badge>
          {importReport.failed > 0 && (
            <Badge className="bg-red-100 text-red-700 hover:bg-red-100">Failed: {importReport.failed}</Badge>
          )}
          <div className="text-[12px] text-slate-500">of {importReport.total} rows</div>
          {importReport.failures.length > 0 && (
            <details className="text-[11px] text-slate-600">
              <summary className="cursor-pointer font-semibold">View {importReport.failures.length} failed row{importReport.failures.length === 1 ? "" : "s"}</summary>
              <div className="mt-2 max-h-40 overflow-y-auto space-y-1">
                {importReport.failures.slice(0, 50).map((f, i) => (
                  <div key={i} className="text-[11px]">Row {f.row}: <span className="text-red-600">{f.reason}</span></div>
                ))}
                {importReport.failures.length > 50 && <div className="text-[11px] italic">…and {importReport.failures.length - 50} more.</div>}
              </div>
            </details>
          )}
          <Button variant="ghost" size="sm" onClick={() => setImportReport(null)} className="ml-auto h-7 text-[11px]">Dismiss</Button>
        </div>
      )}

      {/* Toolbar */}
      <div className="sa-card p-3 flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[280px]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Smart search: name, SKU or barcode…"
            className="pl-9 pr-9 h-10 rounded-lg text-[13.5px] bg-white border-slate-200"
            data-testid="master-products-search"
          />
          {q && (
            <button
              onClick={() => setQ("")}
              className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
              aria-label="Clear search"
              data-testid="master-products-search-clear"
            ><X className="w-3.5 h-3.5" /></button>
          )}
        </div>
        <StoreFilterPicker value={storeFilter} onChange={setStoreFilter} />
        <div className="text-[12px] text-slate-500">
          Showing <span className="font-semibold text-slate-800 tabular-nums">{items.length}</span> of <span className="font-semibold text-slate-800 tabular-nums">{total}</span> · Page {page} / {pageCount}
        </div>
        {orphanCount > 0 && (
          <Badge className="text-white" style={{ backgroundColor: BRAND_RED }} data-testid="master-products-orphan-badge">
            <AlertCircle className="w-3 h-3 mr-1" /> {orphanCount} orphan(s)
          </Badge>
        )}
        {hasFilters && (
          <Button variant="ghost" onClick={clearFilters} className="h-10 rounded-lg text-[12px]" data-testid="master-products-clear-filters">
            <X className="w-3.5 h-3.5 mr-1" /> Clear
          </Button>
        )}
        <Button variant="outline" onClick={load} disabled={loading} className="h-10 rounded-lg" data-testid="master-products-refresh">
          <RefreshCw className={`w-4 h-4 mr-1.5 ${loading ? "animate-spin" : ""}`} /> Refresh
        </Button>
      </div>

      {/* Table */}
      <div className="sa-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-[13px]" data-testid="master-products-table">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr className="text-left text-[11px] font-bold uppercase tracking-wider text-slate-500">
                <th className="px-4 py-3">Product</th>
                <th className="px-4 py-3">Store</th>
                <th className="px-4 py-3">MRP</th>
                <th className="px-4 py-3">Selling Price</th>
                <th className="px-4 py-3">Stock</th>
                <th className="px-4 py-3">Status</th>
              </tr>
            </thead>
            <tbody>
              {loading && items.length === 0 ? (
                Array.from({ length: 8 }).map((_, i) => (
                  <tr key={i} className="border-b border-slate-100 animate-pulse">
                    <td className="px-4 py-3"><div className="h-5 w-40 bg-slate-100 rounded" /></td>
                    <td className="px-4 py-3"><div className="h-5 w-24 bg-slate-100 rounded" /></td>
                    <td className="px-4 py-3"><div className="h-5 w-16 bg-slate-100 rounded" /></td>
                    <td className="px-4 py-3"><div className="h-5 w-16 bg-slate-100 rounded" /></td>
                    <td className="px-4 py-3"><div className="h-5 w-14 bg-slate-100 rounded" /></td>
                    <td className="px-4 py-3"><div className="h-5 w-14 bg-slate-100 rounded" /></td>
                  </tr>
                ))
              ) : items.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-4 py-16 text-center text-slate-400">
                    <Package className="w-10 h-10 mx-auto mb-2 opacity-40" />
                    <div className="text-[14px] font-semibold text-slate-600">No products found</div>
                    <div className="text-[12px] mt-1">Try a different search term or clear filters.</div>
                  </td>
                </tr>
              ) : items.map((it) => (
                <tr key={it.id} className={`border-b border-slate-100 hover:bg-slate-50/60 ${it.is_orphan ? "bg-red-50/40" : ""}`} data-testid={`master-products-row-${it.id}`}>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2.5">
                      <div className="w-10 h-10 rounded-md bg-slate-100 border border-slate-200 grid place-items-center overflow-hidden shrink-0">
                        {it.thumbnail_url
                          ? <img src={it.thumbnail_url} alt="" className="w-full h-full object-cover" />
                          : <Package className="w-4 h-4 text-slate-300" />}
                      </div>
                      <div className="min-w-0">
                        <div className="font-semibold text-slate-900 truncate max-w-[280px]" title={it.name}>{it.name}</div>
                        <div className="text-[11px] text-slate-500 mt-0.5">
                          {it.sku ? `SKU: ${it.sku}` : "No SKU"}{it.has_variants && " · has variants"}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    {it.store ? (
                      <div className="inline-flex items-center gap-1.5 text-[12px] font-semibold text-slate-700">
                        <StoreIcon className="w-3.5 h-3.5 text-slate-400" />
                        <span>{it.store.name}</span>
                        {it.store.code && <span className="text-[10px] text-slate-400 font-normal">· {it.store.code}</span>}
                      </div>
                    ) : (
                      <Badge className="text-white" style={{ backgroundColor: BRAND_RED }} data-testid={`master-products-orphan-${it.id}`}>ORPHAN</Badge>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <EditableNumCell
                      value={it.mrp}
                      prefix="₹"
                      onSave={(v) => patchOne(it.id, { mrp: v }, { mrp: v })}
                      data-testid={`master-products-mrp-${it.id}`}
                    />
                  </td>
                  <td className="px-4 py-3">
                    <EditableNumCell
                      value={it.selling_price}
                      prefix="₹"
                      onSave={(v) => patchOne(it.id, { selling_price: v }, { selling_price: v })}
                      data-testid={`master-products-sp-${it.id}`}
                    />
                  </td>
                  <td className="px-4 py-3">
                    <EditableNumCell
                      value={it.stock ?? 0}
                      onSave={(v) => patchOne(it.id, { stock: v }, { stock: v })}
                      data-testid={`master-products-stock-${it.id}`}
                      step="1"
                    />
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1">
                      <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider ${it.is_active ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>
                        {it.is_active ? "Active" : "Inactive"}
                      </span>
                      {savingId === it.id && <span className="text-[10px] text-amber-600 font-semibold flex items-center gap-0.5"><RefreshCw className="w-2.5 h-2.5 animate-spin" /></span>}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {total > limit && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-slate-200 bg-white text-[12px]">
            <div className="text-slate-500 tabular-nums">Page <span className="font-semibold text-slate-800">{page}</span> of <span className="font-semibold text-slate-800">{pageCount}</span></div>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page <= 1 || loading} className="h-8 rounded-md" data-testid="master-products-prev">Prev</Button>
              <Button variant="outline" size="sm" onClick={() => setPage((p) => Math.min(pageCount, p + 1))} disabled={page >= pageCount || loading} className="h-8 rounded-md" data-testid="master-products-next">Next</Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
